import PageSEO from '@/components/PageSEO';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Phone, Mail, CheckCircle, Sparkles, Shield, Clock, Pencil } from 'lucide-react';
import GoogleReviews from '@/components/GoogleReviews';
import { ShowerDesigner } from '@/components/ShowerDesigner';

export const HomePage = () => {
  const [showDesigner, setShowDesigner] = useState(false);
  const services = [
    {
      title: 'Shower Glass',
      description: 'Custom frameless shower enclosures crafted with precision and style.',
      image: '/images/portfolio/Image-12.jpeg',
      link: '/shower-glass',
    },
    {
      title: 'Wine Enclosures',
      description: 'Elegant glass wine cellars and storage solutions for discerning collectors.',
      image: '/images/portfolio/Image001.jpg',
      link: '/wine-enclosures',
    },
    {
      title: 'Office Partitions',
      description: 'Modern glass office dividers that maximize light and create open workspaces.',
      image: '/images/services/U-channel-Office-Enclosure.jpeg',
      link: '/office-partitions',
    },
    {
      title: 'Mirrors',
      description: 'Framed and frameless mirrors custom-cut to your specifications.',
      image: '/images/portfolio/Image-7-2.jpeg',
      link: '/mirrors',
    },
    {
      title: 'Glass Railings',
      description: 'Stunning glass railings for decks, balconies, and staircases with unobstructed views.',
      image: '/images/portfolio/Image-4.jpeg',
      link: '/glass-railings',
    },
    {
      title: 'Storefront & Curtain Walls',
      description: 'Commercial glass installations that make a lasting impression.',
      image: '/images/services/Impact-Storefront-and-IG-Portfolio.jpg',
      link: '/storefront-curtain-walls',
    },
  ];

  const features = [
    {
      icon: CheckCircle,
      title: 'Effortless Experience',
      description: 'From consultation to installation, we make the process seamless and stress-free.',
    },
    {
      icon: Sparkles,
      title: 'Exceptional Products',
      description: 'Premium glass solutions crafted with meticulous attention to detail.',
    },
    {
      icon: Shield,
      title: 'Professional Crew',
      description: 'Friendly, trained experts committed to excellence in every project.',
    },
    {
      icon: Clock,
      title: 'Ultra-Tight Tolerances',
      description: 'Precision craftsmanship that transforms glass into works of art.',
    },
  ];

  return (
    <div className="min-h-screen">
      <PageSEO title="Custom Glass Solutions for the Texas Hill Country" description="Comal Glass Company provides custom shower enclosures, mirrors, wine room glass, office partitions, storefronts, and glass railings in Bulverde, TX. Serving the Hill Country. Call (830) 359-9925." path="/" />
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Video Background with Overlay */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            poster="/images/portfolio/Image-4-2.jpeg"
          >
            <source src="/images/hero/video-1.mp4" type="video/mp4" />
            {/* Fallback image if video doesn't load */}
            <img
              src="/images/portfolio/Image-4-2.jpeg"
              alt="Luxury frameless shower glass"
              className="w-full h-full object-cover"
            />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        {/* Content */}
        <div className="container-custom relative z-10 text-white">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-primary rounded-full"></div>
              <p className="text-sm sm:text-base text-gray-300 tracking-wide">
                Serving the Texas Hill Country & Surrounding Areas
              </p>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Transform Your Space with
              <span className="text-gradient-cyan"> Custom Glass</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
              Comal Glass Company delivers premium frameless shower glass, mirrors, and architectural glass crafted with precision and innovation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" asChild>
                <Link to="/portfolio">View Our Work</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-primary rounded-full" />
          </div>
        </div>
      </section>

      {/* Shower Designer Modal */}
      <ShowerDesigner isOpen={showDesigner} onClose={() => setShowDesigner(false)} />

      {/* Design Your Shower CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-y border-primary/20">
        <div className="container-custom">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left">
              <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">
                Free Design Tool
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold mb-3">
                Design Your Perfect Shower <span className="text-gradient-cyan">In Minutes</span>
              </h2>
              <p className="text-muted-foreground max-w-xl">
                Use our interactive designer to configure your enclosure type, dimensions, glass, and hardware — then get a personalized quote.
              </p>
            </div>
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 glow-hover text-lg px-8 py-6"
              onClick={() => setShowDesigner(true)}
            >
              <Pencil className="mr-2 h-5 w-5" />
              Start Designing Now
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Why Choose Us</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Experience the <span className="text-gradient-cyan">Comal Glass</span> Difference
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We combine exceptional products with caring service to deliver results that exceed expectations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6 flex flex-col items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground text-sm">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Our Services</Badge>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Discover Our <span className="text-gradient-cyan">Glass Solutions</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From residential to commercial projects, we offer comprehensive glass installation services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link key={index} to={service.link} className="group">
                <Card className="overflow-hidden border-none shadow-md hover:shadow-xl transition-all duration-300 group-hover:scale-[1.02]">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h3 className="text-2xl font-bold mb-2">{service.title}</h3>
                    </div>
                  </div>
                  <CardContent className="pt-6">
                    <p className="text-muted-foreground mb-4">{service.description}</p>
                    <div className="flex items-center text-primary font-medium group-hover:translate-x-2 transition-transform">
                      <span>Learn More</span>
                      <span className="ml-2">→</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">Our Promise</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">
                Clean Job Sites & <span className="text-gradient-cyan">Clear Communication</span>
              </h2>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Professional Crew</h3>
                    <p className="text-muted-foreground text-sm">
                      Friendly and professional team members who ensure everything from consultation to installation is handled efficiently.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Leave It Better</h3>
                    <p className="text-muted-foreground text-sm">
                      We use the campsite principle - leaving every job site cleaner and better than we found it.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1">Precision Craftsmanship</h3>
                    <p className="text-muted-foreground text-sm">
                      Ultra-tight tolerances on all installations to make your new glass stand out as a work of art.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="/images/portfolio/Image-19.jpeg"
                alt="Professional installation"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20">
        <div className="container-custom">
          <GoogleReviews />
        </div>
      </section>

      {/* Associations Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h3 className="text-xl font-semibold mb-8 text-muted-foreground">In Association With</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 items-center justify-items-center">
            <div className="flex items-center justify-center">
              <img 
                src="/images/logos/NAHB.png" 
                alt="NAHB - National Association of Home Builders" 
                className="h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="flex items-center justify-center">
              <img 
                src="/images/logos/GNHBA.png" 
                alt="GNBHBA - Greater New Braunfels Home Builders Association" 
                className="h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="flex items-center justify-center">
              <img 
                src="/images/logos/TAB.png" 
                alt="TAB - Texas Association of Builders" 
                className="h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="flex items-center justify-center">
              <img 
                src="/images/logos/NGA.png" 
                alt="NGA - National Glass Association" 
                className="h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="flex items-center justify-center">
              <img 
                src="/images/logos/TGA.jpg" 
                alt="TGA - Texas Glass Association" 
                className="h-20 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5" />
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Let's Work <span className="text-gradient-cyan">Together</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              We look forward to building something great together.
              <br />
              <em className="text-foreground">- Comal Glass Team</em>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Request Quote</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="tel:8303599925" className="flex items-center space-x-2">
                  <Phone className="h-5 w-5" />
                  <span>(830) 359-9925</span>
                </a>
              </Button>
            </div>
            
            {/* Discreet Easter Egg Link */}
            <div className="mt-8">
              <Link 
                to="/squeegee" 
                className="text-xs text-muted-foreground/50 hover:text-primary transition-colors"
              >
                🚿 Bored? Try the squeegee game
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
